import { makeStyles } from '@material-ui/styles'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import DropdownField from 'core/components/validatedForm/DropdownField'
import TextField from 'core/components/validatedForm/TextField'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

interface StorageVolumesFieldProps {
  volumes?: any[]
  storageClassOptions: any[]
  onChange: (volume: any) => void
}

export default function StorageVolumesField({
  volumes = [],
  storageClassOptions,
  onChange,
}: StorageVolumesFieldProps) {
  const classes = useStyles()

  const handleAddNewStorageVolume = () => {
    const newVolume = {
      id: volumes.length > 0 ? Math.max(...volumes.map((v) => v.id)) + 1 : 1,
      storageClass: '',
      capacity: 0,
    }
    onChange([...volumes, newVolume]) // Create a new array to ensure immutability
  }

  const handleDeleteStorageVolume = (id) => {
    onChange(volumes.filter((volume) => volume.id !== id)) // Filter creates a new array
  }

  return (
    <div className={classes.storageVolumesField}>
      <div className={classes.volumes}>
        {volumes.map((volume) => (
          <div key={volume.id} className={classes.volume}>
            <DropdownField
              id={`storageClass-${volume.id}`}
              label="Storage Class"
              DropdownComponent={AsyncDropdown}
              items={storageClassOptions}
              value={volume.storageClass}
              onChange={(value) =>
                onChange(
                  volumes.map((v) => (v.id === volume.id ? { ...v, storageClass: value } : v)),
                )
              }
              selectFirst
              required
            />
            <div className={classes.row}>
              <TextField
                id={`capacity-${volume.id}`}
                label="Volume Capacity"
                type="number"
                placeholder="GB"
                value={volume.capacity}
                onChange={(value) =>
                  onChange(volumes.map((v) => (v.id === volume.id ? { ...v, capacity: value } : v)))
                }
                className={classes.capacityInput}
                required
              />
              <Text variant="body2" className={classes.unitText}>
                GB
              </Text>
            </div>
            <div className={classes.deleteButton}>
              <FontAwesomeIcon
                className={classes.minusIcon}
                onClick={() => handleDeleteStorageVolume(volume?.id)}
                size="xl"
              >
                minus-circle
              </FontAwesomeIcon>
              <Text variant="body2">Delete Volume</Text>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.addStorageVolumeButton}>
        <FontAwesomeIcon className={classes.plusIcon} onClick={handleAddNewStorageVolume} size="xl">
          plus-circle
        </FontAwesomeIcon>
        <Text variant="body2">Add Storage Volume</Text>
      </div>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  storageVolumesField: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  volumes: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  volume: {
    display: 'grid',
    gridTemplateColumns: '400px 130px max-content',
    gridGap: theme.spacing(4),
    alignItems: 'center',
  },
  addStorageVolumeButton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    margin: theme.spacing(4, 0),
    alignItems: 'center',
  },
  plusIcon: {
    color: theme.palette.blue[600],
    marginRight: theme.spacing(1),
    fontWeight: 900,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '130px max-content',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    position: 'relative',
  },
  unitText: {
    position: 'absolute',
    left: '130px',
    top: '40px',
    marginLeft: theme.spacing(1),
  },
  deleteButton: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(2),
    height: '30px',
    alignSelf: 'end',
    alignItems: 'center',
    marginBottom: '4px',
  },
  minusIcon: {
    color: theme.palette.blue[600],
    fontWeight: 900,
    cursor: 'pointer',
  },
}))
